<section class="wareneingaenge-page">
    <header class="page-header">
        <h1>Wareneingänge</h1>
        <p>Wo muss ich jetzt eingreifen?</p>

        @if (!isLoading() && summaryHandlungsbedarfCount() > 0) {
        <div class="critical-alert">
            <mat-icon fontSet="material-icons-outlined">warning</mat-icon>
            <span>
                {{ summaryHandlungsbedarfCount() }}
                Wareneingänge erfordern sofortige Bearbeitung
            </span>
        </div>
        }
    </header>

    <section class="summary-grid" aria-label="Wareneingänge Übersicht">
        @if (isLoading()) {
        @for (_ of skeletonSummaryCards; track $index) {
        <div class="summary-card summary-card-skeleton">
            <span class="skeleton-line skeleton-number"></span>
            <span class="skeleton-line skeleton-label"></span>
        </div>
        }
        } @else {
        <button type="button" class="summary-card critical"
            [class.summary-card-active-critical]="isSummaryActive('HANDLUNGSBEDARF')"
            (click)="setFilter('HANDLUNGSBEDARF')">
            <strong>{{ summaryHandlungsbedarfCount() }}</strong>
            <span>Handlungsbedarf</span>
        </button>

        <button type="button" class="summary-card"
            [class.summary-card-active-neutral]="isSummaryActive(WareneingangStatus.Offen)"
            (click)="setFilter(WareneingangStatus.Offen)">
            <strong>{{ summaryOffenCount() }}</strong>
            <span>Offen</span>
        </button>

        <button type="button" class="summary-card"
            [class.summary-card-active-orange]="isSummaryActive(WareneingangStatus.InBearbeitung)"
            (click)="setFilter(WareneingangStatus.InBearbeitung)">
            <strong>{{ summaryInBearbeitungCount() }}</strong>
            <span>In Bearbeitung</span>
        </button>

        <div class="summary-card summary-card-disabled">
            <strong>{{ summaryOffenePositionenGesamt() }}</strong>
            <span>Positionen offen</span>
        </div>
        }
    </section>

    @if (isLoading()) {
    <section class="toolbar toolbar-skeleton">
        <div class="filiale-trigger filiale-trigger-skeleton">
            <span class="skeleton-line skeleton-icon"></span>
            <span class="skeleton-line skeleton-select"></span>
            <span class="skeleton-line skeleton-icon"></span>
        </div>

        <div class="filter-tabs">
            @for (_ of skeletonFilterTabs; track $index) {
            <div class="filter-tab filter-tab-skeleton">
                <span class="skeleton-line skeleton-filter-label"></span>
                <span class="skeleton-line skeleton-filter-count"></span>
            </div>
            }
        </div>
    </section>
    } @else {
    <section class="toolbar">
        <label class="filiale-select">
            <button type="button" class="filiale-trigger" (click)="toggleFilialeDropdown()">
                <mat-icon fontSet="material-icons-outlined">storefront</mat-icon>
                <span>{{ selectedFilialeLabel() }}</span>
                <mat-icon class="select-arrow" fontSet="material-icons-outlined">
                    {{ isFilialeDropdownOpen() ? 'expand_less' : 'expand_more' }}
                </mat-icon>
            </button>

            @if (isFilialeDropdownOpen()) {
            <div class="filiale-dropdown">
                @for (filiale of filialen(); track filiale.value) {
                <button type="button" class="filiale-option"
                    [class.filiale-option-active]="selectedFiliale() === filiale.value"
                    (click)="selectFiliale(filiale.value)">
                    {{ filiale.label }}
                </button>
                }
            </div>
            }
        </label>

        <div class="filter-tabs">
            @for (filter of statusFilters(); track filter.value) {
            <button type="button" class="filter-tab" [class.filter-tab-active]="selectedFilter() === filter.value"
                (click)="setFilter(filter.value)">
                <span>{{ filter.label }}</span>
                <strong>{{ filter.count }}</strong>
            </button>
            }
        </div>
    </section>
    }

    <section class="overview-table">
        @if (isLoading()) {
        <div class="table-header">
            <span class="skeleton-line skeleton-header"></span>
            <span class="skeleton-line skeleton-header"></span>
            <span class="skeleton-line skeleton-header"></span>
            <span class="skeleton-line skeleton-header"></span>
            <span class="skeleton-line skeleton-header"></span>
            <span class="skeleton-line skeleton-header"></span>
            <span class="skeleton-line skeleton-header"></span>
        </div>
        } @else if (filteredWareneingaenge().length) {
        <div class="table-header">
            <button type="button" class="table-sort-button" [class.table-sort-button-active]="isSortedBy('filiale')"
                (click)="setSort('filiale')">
                <span>Filiale</span>

                <mat-icon fontSet="material-icons-outlined">
                    {{ getSortIcon('filiale') }}
                </mat-icon>
            </button>

            <span>Lieferschein</span>
            <span>Fälligkeit</span>

            <button type="button" class="table-sort-button status-header-button"
                [class.table-sort-button-active]="isSortedBy('status')" (click)="setSort('status')">
                <span>Status</span>

                <mat-icon fontSet="material-icons-outlined">
                    {{ getSortIcon('status') }}
                </mat-icon>
            </button>

            <button type="button" class="table-sort-button" [class.table-sort-button-active]="isSortedBy('positionen')"
                (click)="setSort('positionen')">
                <span>Positionen</span>

                <mat-icon fontSet="material-icons-outlined">
                    {{ getSortIcon('positionen') }}
                </mat-icon>
            </button>

            <span>Fortschritt</span>
            <span>Aktion</span>
        </div>
        }

        <div class="table-body">
            @if (isLoading()) {
            @for (_ of skeletonRows; track $index) {
            <div class="table-row table-row-skeleton">
                <div class="cell">
                    <span class="skeleton-line skeleton-title"></span>
                    <span class="skeleton-line skeleton-subtitle"></span>
                </div>

                <div class="cell">
                    <span class="skeleton-line skeleton-title"></span>
                    <span class="skeleton-line skeleton-subtitle"></span>
                </div>

                <div class="cell">
                    <span class="skeleton-line skeleton-short"></span>
                </div>

                <div class="cell">
                    <span class="skeleton-line skeleton-pill"></span>
                </div>

                <div class="cell">
                    <span class="skeleton-line skeleton-medium"></span>
                </div>

                <div class="cell">
                    <span class="skeleton-line skeleton-progress"></span>
                </div>

                <div class="cell">
                    <span class="skeleton-line skeleton-action"></span>
                </div>
            </div>
            }
            } @else if (!filteredWareneingaenge().length) {
            <div class="empty-state">
                Keine Wareneingänge im aktuellen Filter vorhanden.
            </div>
            } @else {
            @for (
            wareneingang of filteredWareneingaenge();
            track wareneingang.id
            ) {
            <div class="table-row table-row-clickable" tabindex="0" (click)="openWareneingang(wareneingang)">
                <div class="cell filiale-cell">
                    <strong>{{ wareneingang.filialeName }}</strong>
                    <small> {{ wareneingang.filialNummer }}
                    </small>
                </div>

                <div class="cell">
                    <strong>{{ wareneingang.lieferscheinnummer }}</strong>

                    <small class="lieferschein-date-full">
                        {{ formatDisplayDate(wareneingang.wareneingangTag) }}
                    </small>

                    <small class="lieferschein-date-compact">
                        {{ formatCompactDisplayDate(wareneingang.wareneingangTag) }}
                    </small>
                </div>

                <div class="cell">
                    <div class="faelligkeit-content">
                        <span [class]="
                                    getFaelligkeitClass(
                                        getLiveFaelligkeitStatus(wareneingang)
                                    )
                                ">
                            {{ getLiveFaelligkeitLabel(wareneingang) }}
                        </span>

                        @if (
                        getFaelligkeitRestzeit(wareneingang);
                        as restzeit
                        ) {
                        <small class="faelligkeit-restzeit" [class.faelligkeit-restzeit-ueberfaellig]="
                                        getLiveFaelligkeitStatus(wareneingang) ===
                                        'UEBERFAELLIG'
                                    ">
                            {{ restzeit }}
                        </small>
                        }
                    </div>
                </div>

                <div class="cell status-cell">
                    <span class="status-pill" [ngClass]="getStatusClass(wareneingang.status)">
                        {{ getStatusLabel(wareneingang.status) }}
                    </span>
                </div>

                <div class="cell">
                    <span class="position-count" [class.position-count-critical]="
                                wareneingang.status ===
                                    WareneingangStatus.Offen &&
                                wareneingang.offenePositionen > 0
                            " [class.position-count-neutral]="
                                wareneingang.status ===
                                WareneingangStatus.InBearbeitung
                            " [class.position-count-complete]="
                                wareneingang.status ===
                                WareneingangStatus.Abgeschlossen
                            ">
                        @if (
                        wareneingang.status ===
                        WareneingangStatus.Abgeschlossen
                        ) {
                        Alle erfasst
                        } @else {
                        {{ wareneingang.offenePositionen }} von
                        {{ wareneingang.anzahlPositionen }} offen
                        }
                    </span>
                </div>

                <div class="cell progress-cell" [class.progress-offen]="
                            wareneingang.status === WareneingangStatus.Offen
                        " [class.progress-in-bearbeitung]="
                            wareneingang.status ===
                            WareneingangStatus.InBearbeitung
                        " [class.progress-abgeschlossen]="
                            wareneingang.status ===
                            WareneingangStatus.Abgeschlossen
                        " [class.progress-complete]="
                            wareneingang.fortschritt >= 100
                        ">
                    <div class="progress-track">
                        <span class="progress-fill" [style.width.%]="wareneingang.fortschritt"></span>
                    </div>

                    <strong>{{ wareneingang.fortschritt }}%</strong>
                </div>

                <div class="cell action-cell">
                    <button type="button" (click)="
                                $event.stopPropagation();
                                openWareneingang(wareneingang)
                            ">
                        {{ getActionLabel(wareneingang) }}
                    </button>
                </div>
            </div>
            }
            }
        </div>
    </section>

    @if (isLoading()) {
    <footer class="overview-footer overview-footer-skeleton">
        <span class="skeleton-line skeleton-footer-item footer-item-inline"></span>
        <span class="skeleton-line skeleton-footer-item footer-item-inline"></span>
        <span class="skeleton-line skeleton-footer-item footer-item-block"></span>
    </footer>
    } @else {
    <footer class="overview-footer">
        <span class="footer-item-inline">Gesamt: <strong>{{ totalCount() }}</strong></span>
        <span class="footer-item-inline">Erledigt: <strong>{{ erledigtCount() }}/{{ totalCount() }} ({{ erledigtPercent() }}%)</strong></span>
        <span class="footer-item-block">Positionen offen: <strong>{{ offenePositionenGesamt() }}</strong></span>
    </footer>
    }
</section>